import { Box, Button, CircularProgress } from '@ouster/webviz-react-components';

import { useEffect, useState } from 'react';
import { RiUserSearchLine } from 'react-icons/ri';
import UserDelete from 'src/components/adminDashboard/UserDelete';
import useChakraHook from 'src/hooks/useChakraHook';

import { createAlert } from './DevToolRecordingsMgmt';
import DashboardCard from '../../components/adminDashboard/DashboardCard';
import UserGdprCheckResult from '../../components/adminDashboard/UserGdprCheckResult';
import UserLookUpEula from '../../components/adminDashboard/UserLookUpEula';
import UserLookUpKeycloakInfo from '../../components/adminDashboard/UserLookUpKeycloakInfo';
import UserLookUpOrganization from '../../components/adminDashboard/UserLookUpOrganization';
import UserLookUpSearch from '../../components/adminDashboard/UserLookUpSearch';
import UserLookUpStudioInfo from '../../components/adminDashboard/UserLookUpStudioInfo';
import UserUploaderStatusSync from '../../components/adminDashboard/UserUploaderStatusSync';
import { useStoreActions, useStoreState } from '../../reducers';

const EMAIL_VERIFICATION_UPDATE_BATCH_SIZE = 50;
const BATCH_PROCESSING_DELAY_MS = 200; // Delay between batch requests

export default function UserLookUp() {
  const {
    userLookupResult,
    isLoadingUserLookup,
    isLoadingGdprDataCheck,
    verifiedEmailSyncProgress,
    compareUsersOrgsResult,
    gdprDataCheckResult: userGdprInfo,
  } = useStoreState((storeState) => storeState.adminDashboard);

  const {
    checkUserDataForGdpr,
    getUserInfoByEmail,
    clearUserLookup,
    clearGdprUserLookup,
    syncStudioEmailVerificationToKeycloak,
    setVerifiedEmailSyncProgress,
    resetSyncVerifiedEmailProgress,
    compareUsersOrgsWithUploader,
    setGdprDataCheckResult: setUserGdprInfo,
    deleteUserGdprCompliant,
  } = useStoreActions((actions) => actions.adminDashboard);

  const [toolMode, setToolMode] = useState<
    'search' | 'syncEmailVerification' | 'checkUploaderStatus' | 'gdprCheck'
  >('search');
  const [isSyncVerifiedEmail, setIsSyncVerifiedEmail] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<JSX.Element | null>(
    null
  );

  const { showToast } = useChakraHook();

  useEffect(() => {
    let isCancelled = false;
    let isProcessing = false;

    const processBatch = async () => {
      if (!verifiedEmailSyncProgress.isInProgress || isProcessing) {
        return;
      }
      isProcessing = true;

      try {
        const result = await syncStudioEmailVerificationToKeycloak({
          page: verifiedEmailSyncProgress.currentPage,
          perPage: EMAIL_VERIFICATION_UPDATE_BATCH_SIZE,
        });

        if (isCancelled) return;

        const newTotalUpdated =
          verifiedEmailSyncProgress.totalUpdated + result.updated;

        if (result.completed) {
          // Process completed
          const alertElement =
            newTotalUpdated === 0
              ? createAlert('info', 'No users needed updating.', true)
              : createAlert(
                  'success',
                  `${newTotalUpdated} users synced successfully.`,
                  true
                );
          setFeedbackMessage(alertElement);
          resetSyncVerifiedEmailProgress();
          setIsSyncVerifiedEmail(false);
        } else {
          // Continue to next page
          setTimeout(() => {
            if (!isCancelled) {
              setVerifiedEmailSyncProgress({
                totalUpdated: newTotalUpdated,
                currentPage: verifiedEmailSyncProgress.currentPage + 1,
                isInProgress: true,
                progressPercentage: result.progress_percentage || 0,
              });
            }
          }, BATCH_PROCESSING_DELAY_MS); // Small delay between requests
        }
      } catch (error) {
        if (!isCancelled) {
          setFeedbackMessage(
            createAlert(
              'error',
              `Failed to sync user's email verification status. ${error}`,
              true
            )
          );
          resetSyncVerifiedEmailProgress();
          setIsSyncVerifiedEmail(false);
        }
      }
    };

    if (toolMode === 'syncEmailVerification') processBatch();

    return () => {
      isCancelled = true;
    };
  }, [
    verifiedEmailSyncProgress.isInProgress,
    verifiedEmailSyncProgress.currentPage,
    verifiedEmailSyncProgress.totalUpdated,
    syncStudioEmailVerificationToKeycloak,
    setVerifiedEmailSyncProgress,
    resetSyncVerifiedEmailProgress,
    toolMode,
  ]);

  const verifyEmail = () => {
    // clearUserLookup();

    setIsSyncVerifiedEmail(true);
    setFeedbackMessage(null);

    setToolMode('syncEmailVerification');
    setVerifiedEmailSyncProgress({
      totalUpdated: 0,
      currentPage: 1,
      isInProgress: true,
      progressPercentage: 0,
    });
  };

  const uploaderStatusCheck = () => {
    setIsSyncVerifiedEmail(false);
    setFeedbackMessage(null);
    setToolMode('checkUploaderStatus');

    compareUsersOrgsWithUploader();
  };

  const handleSearch = async (email: string) => {
    setToolMode('search');
    setIsSyncVerifiedEmail(false);
    setFeedbackMessage(null);
    try {
      await getUserInfoByEmail(email);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClearUserLookup = () => {
    clearUserLookup();
  };

  const handleClearGdprInfo = () => {
    clearGdprUserLookup();
    setToolMode('search');
  };

  const handleGdprUserSearch = async (email: string) => {
    setToolMode('gdprCheck');
    setIsSyncVerifiedEmail(false);
    setFeedbackMessage(null);
    setUserGdprInfo(null);
    try {
      await checkUserDataForGdpr(email);
    } catch (error) {
      console.error('GDPR check failed:', error);
    }
  };

  const handleDeleteUser = async (email: string): Promise<boolean> => {
    try {
      await deleteUserGdprCompliant(email);
      showToast({
        title: 'User deleted successfully',
        description: `User with email ${email} has been deleted.`,
        status: 'success',
      });
      clearGdprUserLookup();
      return true;
    } catch (error) {
      showToast({
        title: 'User deletion failed',
        description: `${error}`,
        status: 'error',
      });
      return false;
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 5fr"
      height="100%"
      overflow="hidden"
    >
      <DashboardCard
        icon={<RiUserSearchLine size={18} />}
        title="User Management Console"
        data-testid="user-management-console-card"
        style={{
          alignContent: 'start',
          overflow: 'hidden',
          borderRadius: '0px',
        }}
      >
        <Box display="grid" gap={4} overflow="auto">
          <UserLookUpSearch
            handleSearch={handleSearch}
            clearUserLookup={handleClearUserLookup}
          />
          <UserDelete
            handleSearch={handleGdprUserSearch}
            handleDelete={handleDeleteUser}
            clearGdprInfo={handleClearGdprInfo}
            gdprInfo={userGdprInfo}
          />
          <DashboardCard
            title="Email verification"
            description="Sync email verification status from Studio to Keycloak"
            data-testid="email-verification-console-card"
          >
            <Button
              variant="outline"
              onClick={verifyEmail}
              minWidth="min-content"
            >
              Verify Email
            </Button>
          </DashboardCard>
          <DashboardCard
            title="Uploader Status Check"
            description="Find the cases where uploader is not synced with Studio"
            data-testid="uploader-status-check-card"
          >
            <Button
              variant="outline"
              onClick={uploaderStatusCheck}
              minWidth="min-content"
            >
              Check Status
            </Button>
          </DashboardCard>
        </Box>
      </DashboardCard>

      {toolMode === 'search' &&
        (isLoadingUserLookup || userLookupResult !== null) && (
          <DashboardCard
            title="User Details"
            data-testid="user-detail-card"
            style={{
              height: '100%',
              overflow: 'auto',
              borderRadius: '0px',
              alignContent: 'start',
            }}
          >
            <Box rowGap="0.75rem" display="grid" height="100%" overflow="auto">
              <UserLookUpKeycloakInfo
                isUserFound={userLookupResult !== null}
                keycloakUserInfo={userLookupResult?.auth_server_info ?? null}
                isLoadingUserLookup={isLoadingUserLookup}
              />
              <UserLookUpStudioInfo
                isUserFound={userLookupResult !== null}
                studioUserInfo={userLookupResult?.user ?? null}
                isLoadingUserLookup={isLoadingUserLookup}
              />
              <UserLookUpEula
                isUserFound={userLookupResult !== null}
                eulaInfo={userLookupResult?.eula_information ?? null}
                isLoadingUserLookup={isLoadingUserLookup}
              />
              <UserLookUpOrganization
                organizations={userLookupResult?.organizations ?? []}
                isLoadingUserLookup={isLoadingUserLookup}
                isUserFound={userLookupResult !== null}
              />
            </Box>
          </DashboardCard>
        )}

      {toolMode === 'syncEmailVerification' && (
        <Box display="grid" placeContent="center">
          {isSyncVerifiedEmail ? (
            <Box display="grid" gap={8} justifyItems="center">
              <Box as="p" fontSize="xl">
                Syncing email verification status from Studio to Keycloak.
                Please wait...
              </Box>
              <Box as="p" fontSize="lg">
                Progress: {verifiedEmailSyncProgress.totalUpdated} users&apos;
                email verification status have been updated
              </Box>
              <CircularProgress
                isIndeterminate={
                  verifiedEmailSyncProgress.progressPercentage === 0
                }
                value={verifiedEmailSyncProgress.progressPercentage}
                size={20}
              />
            </Box>
          ) : (
            feedbackMessage
          )}
        </Box>
      )}

      {toolMode === 'checkUploaderStatus' && (
        <UserUploaderStatusSync
          compareUsersOrgsResult={compareUsersOrgsResult}
        />
      )}

      {toolMode === 'gdprCheck' &&
        (isLoadingGdprDataCheck || userGdprInfo !== null) && (
          <UserGdprCheckResult
            isLoading={isLoadingGdprDataCheck}
            gdprInfo={userGdprInfo}
          />
        )}

      {toolMode === 'search' && !userLookupResult && (
        <Box
          border="1px solid"
          borderColor="whiteAlpha.200"
          bgColor="whiteAlpha.50"
          display="grid"
          gap={6}
          placeContent="center"
          justifyItems="center"
          data-testid="recording-lookup-no-result-box"
        >
          <Box fontSize="2xl" color="gray.300">
            User management console
          </Box>
        </Box>
      )}
    </Box>
  );
}
